import { NotificationLog } from 'domain/models';
import {
	AlertDefinition,
	Crop,
	Station,
	VirtualStation,
	User,
	Farm,
} from 'domain/models/api.models';
import { useHal } from '../../hooks/hal/hal.service';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Mark } from 'pages/observations/ObservationSide';

export const useAlertsPanel = () => {
	return useHal<AlertDefinition>('panel');
};

export const useCrops = () => {
	return useHal<Crop>('crops');
};

export const useFarms = () => {
	return useHal<Farm>('farms');
};

export const useUsers = () => {
	return useHal<User>('users');
};

export const useStations = () => {
	return useHal<Station>('stations');
};

export const useVirtualStations = () => {
	return useHal<VirtualStation>('virtual-stations');
};

export const useAlerts = () => {
	return useHal<AlertDefinition>('alert-definitions');
};

export const useDerived = () => {
	return useHal<AlertDefinition>('derived');
};

export const useAdminAlerts = () => {
	return useHal<NotificationLog>('alert-notifications');
};

export const RELS_TYPES = {
	panel: useAlertsPanel,
	dashboards: useVirtualStations,
	virtualStations: useVirtualStations,
	crops: useCrops,
	farms: useFarms,
	users: useUsers,
	stations: useStations,
	alerts: useAlerts,
	derived: useDerived,
	adminAlerts: useAdminAlerts,
};

export const getEditLink = (rel: string): string => {
	if (rel === 'dashboards') {
		return '/edit/virtual-stations';
	}
	if (rel === 'alerts') {
		return '/edit/alert-definitions';
	}
	return `/edit/${rel}`;
};

export type RelsTypes = keyof typeof RELS_TYPES;

export const useStationsPositions = (ids: (number | string)[]) => {
	// On id change, update the map info
	const { findOne } = useStations();

	const idsRef = useRef<(number | string)[]>([]);

	const currentIds = useMemo(() => ids ?? [], [ids]);

	const [currentPositions, setCurrentPositions] = useState<{
		[id: string]: Mark;
	}>({});

	useEffect(() => {
		// If the ids are the same, do nothing
		if (JSON.stringify(idsRef.current) === JSON.stringify(currentIds)) {
			return;
		}

		// Otherwise, update the ids ref
		idsRef.current = currentIds;

		const promises = currentIds.map((id: number | string) => {
			const found = findOne(id as string);
			return found
				.then((res) => {
					return {
						lat: res?.latitude,
						lng: res?.longitude,
						id: id,
						name: res?.name,
					} as Mark;
				})
				.catch((err) => {
					return undefined;
				});
		});
		Promise.all(promises).then((res: (Mark | undefined)[]) => {
			const result = res.filter((r) => r !== undefined) as Mark[];
			const p: { [id: string]: Mark } = {};
			result.forEach((r, index) => {
				p[r.name ?? 'r-' + index] = r;
			});
			setCurrentPositions(p);
		});
	}, [currentIds]);

	return currentPositions;
};

export const useVirtualStationsPositions = () => {
	// On id change, update the map info
	const { find } = useVirtualStations();

	const [currentPositions, setCurrentPositions] = useState<{
		[id: string]: Mark;
	}>({});

	useEffect(() => {
		const found = find();
		found
			.then((res) => {
				const s = res.content.map((r) => {
					return {
						lat: r.latitude,
						lng: r.longitude,
						id: r.name,
						name: r.name,
					} as Mark;
				});
				const p: { [id: string]: Mark } = {};
				s.forEach((r, index) => {
					p[r.name ?? 'r-' + index] = r;
				});
				setCurrentPositions(p);
			})
			.catch((err) => {
				return undefined;
			});
	}, []);

	return currentPositions;
};
